import { Button } from "@/components/ui/button";
import spotlessLogo from "@/assets/spotless-polska-logo.webp";
import { Link } from "@/lib/router-compat";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSeo } from "@/hooks/useSeo";
import { buildLocalizedSeo } from "@/lib/pageSeo";
import { META_DESCRIPTIONS } from "@/lib/metaDescriptions";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const CALENDLY_URL = "https://calendly.com/dariusz-potepa-agapeco/discoverycall";

const content = {
  en: {
    crumbRoot: "Case Studies",
    home: "/",
    h1: "Spotless Polska",
    sub: "How we built a B2B sales foundation from scratch in 5 weeks.",
    lead: [
      "In ", ["5 weeks"], " we built the Spotless Polska brand from scratch: from the domain to the first B2B campaigns. The client received a ready sales foundation: brand, website, mailbox and a database of about ", ["1,000"], " companies. First send in the PV farms segment: ", ["144"], " companies, ", ["4"], " interested.",
    ],
    stats: [
      { value: "5 wks", label: "pilot duration" },
      { value: "~1,000", label: "companies in the database" },
      { value: "4", label: "campaign segments" },
      { value: "144 → 4", label: "companies and interested firms (PV farms)" },
    ],
    note: "Figures from the campaign panel, as of September 2026.",
    challengeLabel: "Challenge",
    challenge: ["Marcin was entering the market with chemical-free photovoltaic panel cleaning. He had the technology (Mansser Osmo, working from the ground up to ", ["20 m"], "), but no brand, website, price list or references. He needed to test demand, not a pretty company presentation."],
    solutionLabel: "Solution on a timeline",
    timeline: [
      { when: "Week 1", what: "Brand description, sales arguments, ideal customer profile, marketing plan. Domain, mailbox, mailbox reputation warm-up. Logo and business card." },
      { when: "Week 3", what: "Campaign launch in the 3 best segments: PV farms, car dealerships, warehouses. Flyer ready." },
      { when: "Week 5", what: "Pilot analysis and summary." },
    ],
    resultsLabel: "Results",
    results: [
      ["In total: ", ["743"], " emails to ", ["385"], " companies, ", ["38"], " replies (", ["9.9%"], "), ", ["4"], " quote requests."],
      ["PV farms: ", ["144"], " companies, ", ["278"], " emails, ", ["11"], " replies, ", ["4"], " interested companies."],
      ["Database of about ", ["1,000"], " companies."],
      [["4"], " ready segment campaigns."],
    ],
    learnedLabel: "What we learned",
    learned: "The client asked for two emails (first contact and a reminder after 7 days) instead of AGAPE's standard three. PV farms responded best and delivered most of the quote requests.",
    ctaTitle: "Want this foundation for your company?",
    ctaBody: "Let's talk about your goals. 15 minutes, no obligation.",
    cta: "Book a call",
  },
  pl: {
    crumbRoot: "Case Studies",
    home: "/pl",
    h1: "Spotless Polska",
    sub: "Jak w 5 tygodni zbudowaliśmy fundament sprzedaży do firm od zera.",
    lead: [
      "W ", ["5 tygodni"], " zbudowaliśmy markę Spotless Polska od zera: od domeny po pierwsze kampanie do firm. Klient dostał gotowy fundament sprzedaży: markę, stronę internetową, skrzynkę mailową i bazę ok. ", ["1000"], " firm. Pierwsza wysyłka w segmencie farm fotowoltaicznych: ", ["144"], " firmy, ", ["4"], " zainteresowane.",
    ],
    stats: [
      { value: "5 tyg.", label: "czas pilotażu" },
      { value: "ok. 1000", label: "firm w bazie" },
      { value: "4", label: "segmenty kampanii" },
      { value: "144 → 4", label: "firmy i zainteresowane (farmy fotowoltaiczne)" },
    ],
    note: "Dane z panelu kampanii, stan na wrzesień 2026.",
    challengeLabel: "Wyzwanie",
    challenge: ["Marcin wchodził na rynek z usługą mycia paneli fotowoltaicznych bez chemii. Miał technologię (Mansser Osmo, praca z ziemi do ", ["20 m"], "), ale nie miał marki, strony, cennika ani referencji. Potrzebował sprawdzenia popytu, a nie ładnej prezentacji firmy."],
    solutionLabel: "Rozwiązanie w osi czasu",
    timeline: [
      { when: "Tydzień 1", what: "Opis marki, argumenty sprzedażowe, opis idealnego klienta, plan marketingowy. Domena, skrzynka, start budowania reputacji skrzynki. Logo i wizytówka." },
      { when: "Tydzień 3", what: "Start kampanii w 3 najlepszych segmentach: farmy fotowoltaiczne, salony samochodowe, hale magazynowe. Ulotka gotowa." },
      { when: "Tydzień 5", what: "Analiza i podsumowanie pilotażu." },
    ],
    resultsLabel: "Wyniki",
    results: [
      ["Łącznie: ", ["743"], " maile do ", ["385"], " firm, ", ["38"], " odpowiedzi (", ["9,9%"], "), ", ["4"], " zapytań ofertowych."],
      ["Farmy fotowoltaiczne: ", ["144"], " firmy, ", ["278"], " maili, ", ["11"], " odpowiedzi, ", ["4"], " zainteresowane firmy."],
      ["Baza ok. ", ["1000"], " firm."],
      [["4"], " gotowe kampanie segmentowe."],
    ],
    learnedLabel: "Czego się nauczyliśmy",
    learned: "Klient poprosił o dwa maile (pierwszy i przypominający po 7 dniach) zamiast trzech, jak w standardzie AGAPE. Najlepiej odpowiadały farmy fotowoltaiczne i to one dały większość zapytań.",
    ctaTitle: "Chcesz taki fundament dla swojej firmy?",
    ctaBody: "Porozmawiajmy o Twoich celach. 15 minut, bez zobowiązań.",
    cta: "Umów rozmowę",
  },
  by: {
    crumbRoot: "Кейсы",
    home: "/by",
    h1: "Spotless Polska",
    sub: "Як за 5 тыдняў мы з нуля стварылі падмурак B2B-продажаў.",
    lead: [
      "За ", ["5 тыдняў"], " мы стварылі марку Spotless Polska з нуля: ад дамена да першых кампаній для фірмаў. Кліент атрымаў гатовы падмурак продажаў: марку, сайт, паштовую скрыню і базу каля ", ["1000"], " фірмаў. Першая рассылка ў сегменце сонечных ферм: ", ["144"], " фірмы, ", ["4"], " зацікаўленыя.",
    ],
    stats: [
      { value: "5 тыд.", label: "працягласць пілота" },
      { value: "~1000", label: "фірмаў у базе" },
      { value: "4", label: "сегменты кампаній" },
      { value: "144 → 4", label: "фірмы і зацікаўленыя (сонечныя фермы)" },
    ],
    note: "Даныя з панэлі кампаній, стан на верасень 2026.",
    challengeLabel: "Выклік",
    challenge: ["Марцін выходзіў на рынак з мыццём фотаэлектрычных панэляў без хіміі. Ён меў тэхналогію (Mansser Osmo, праца з зямлі да ", ["20 м"], "), але не меў маркі, сайта, прайса і рэкамендацый. Яму трэба было праверыць попыт, а не прыгожую прэзентацыю."],
    solutionLabel: "Рашэнне па тыднях",
    timeline: [
      { when: "Тыдзень 1", what: "Апісанне маркі, аргументы продажу, профіль ідэальнага кліента, маркетынгавы план. Дамен, скрыня, прагрэў рэпутацыі скрыні. Лагатып і візітоўка." },
      { when: "Тыдзень 3", what: "Старт кампаній у 3 найлепшых сегментах: сонечныя фермы, аўтасалоны, склады. Улётка гатовая." },
      { when: "Тыдзень 5", what: "Аналіз і падсумаванне пілота." },
    ],
    resultsLabel: "Вынікі",
    results: [
      ["Усяго: ", ["743"], " лісты да ", ["385"], " фірмаў, ", ["38"], " адказаў (", ["9,9%"], "), ", ["4"], " запытаў на прапанову."],
      ["Сонечныя фермы: ", ["144"], " фірмы, ", ["278"], " лістоў, ", ["11"], " адказаў, ", ["4"], " зацікаўленыя фірмы."],
      ["База каля ", ["1000"], " фірмаў."],
      [["4"], " гатовыя сегментныя кампаніі."],
    ],
    learnedLabel: "Што мы зразумелі",
    learned: "Кліент папрасіў два лісты (першы і напамін праз 7 дзён) замест стандартных для AGAPE трох. Лепш за ўсё адказвалі сонечныя фермы, і яны далі большасць запытаў.",
    ctaTitle: "Хочаце такі падмурак для сваёй фірмы?",
    ctaBody: "Пагаворым пра вашыя мэты. 15 хвілін, без абавязацельстваў.",
    cta: "Запісацца на Спатканне",
  },
};

type Rich = ReadonlyArray<string | ReadonlyArray<string>>;
const RichText = ({ parts }: { parts: Rich }) => (
  <>
    {parts.map((p, i) =>
      typeof p === "string" ? <span key={i}>{p}</span> : <strong key={i} className="font-semibold text-foreground">{p[0]}</strong>,
    )}
  </>
);

const CaseStudySpotless = () => {
  const { language } = useLanguage();
  const t = content[language] ?? content.en;

  useSeo(
    buildLocalizedSeo({
      slug: "case-studies/spotless-polska",
      language,
      ogType: "article",
      meta: {
        en: { title: "Spotless Polska case study — AGAPE", description: META_DESCRIPTIONS.spotless.en },
        pl: { title: "Case study Spotless Polska — AGAPE", description: META_DESCRIPTIONS.spotless.pl },
        by: { title: "Кейс Spotless Polska — AGAPE", description: META_DESCRIPTIONS.spotless.by },
      },
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: `${t.h1}: ${t.sub}`,
          about: "Spotless Polska",
          author: { "@type": "Organization", name: "AGAPE" },
          publisher: { "@type": "Organization", name: "AGAPE", url: "https://agapeco.net" },
        },
      ],
    }),
  );

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-28 pb-16">
        <article className="mx-auto max-w-[820px] px-6">
          <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-5">
            <Link to={`${t.home === "/" ? "" : t.home}/#case-studies`} className="hover:text-foreground transition-colors">
              {t.crumbRoot}
            </Link>
            <span aria-hidden="true"> → </span>
            <span className="text-foreground/80">{t.h1}</span>
          </nav>

          <div className="rounded-xl border border-border bg-gradient-to-br from-primary/30 via-primary/15 to-accent/20 p-6 md:p-8 mb-8 flex items-center">
            <div className="rounded-lg bg-card-foreground px-4 py-3">
              <img src={spotlessLogo} alt="Spotless Polska logo" width={480} height={392} className="h-20 md:h-24 w-auto" />
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">{t.h1}</h1>
          <p className="text-lg text-muted-foreground mb-6">{t.sub}</p>

          <p className="text-lg text-foreground/90 leading-relaxed border-l-[3px] border-primary pl-4 my-6">
            <RichText parts={t.lead} />
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            {t.stats.map((s, i) => (
              <div key={i} className="rounded-lg border border-border bg-card p-4 text-center">
                <p className="text-2xl font-bold text-foreground">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-snug">{s.label}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">{t.note}</p>

          <h2 className="text-sm font-bold uppercase tracking-widest text-primary mt-10 mb-3">{t.challengeLabel}</h2>
          <p className="text-foreground/75 leading-relaxed"><RichText parts={t.challenge} /></p>

          <h2 className="text-sm font-bold uppercase tracking-widest text-primary mt-10 mb-4">{t.solutionLabel}</h2>
          <ol className="relative">
            {t.timeline.map((item, i) => (
              <li key={i} className="relative flex gap-4 pb-5">
                {i < t.timeline.length - 1 && (
                  <span aria-hidden="true" className="absolute left-14 top-8 bottom-0 w-0.5 bg-primary/40" />
                )}
                <span className="flex-none w-28 h-8 rounded-lg border border-primary/40 bg-primary/15 text-sm font-semibold text-foreground flex items-center justify-center">
                  {item.when}
                </span>
                <p className="text-foreground/75 leading-relaxed pt-1">{item.what}</p>
              </li>
            ))}
          </ol>

          <h2 className="text-sm font-bold uppercase tracking-widest text-primary mt-10 mb-3">{t.resultsLabel}</h2>
          <ul className="space-y-1 text-foreground/75">
            {t.results.map((r, i) => (
              <li key={i}>· <RichText parts={r} /></li>
            ))}
          </ul>

          <h2 className="text-sm font-bold uppercase tracking-widest text-primary mt-10 mb-3">{t.learnedLabel}</h2>
          <p className="text-foreground/75 leading-relaxed">{t.learned}</p>

          <section className="mt-12 rounded-xl border border-primary/40 bg-gradient-to-br from-primary/15 to-primary/5 p-7 text-center">
            <h2 className="text-xl font-bold text-foreground mb-2">{t.ctaTitle}</h2>
            <p className="text-muted-foreground mb-4">{t.ctaBody}</p>
            <Button asChild className="font-semibold">
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">{t.cta}</a>
            </Button>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default CaseStudySpotless;
